import React, { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Paper, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { pluralize, capitalize } from "inflection";
import csrfToken from "../lib/csrf-token";
import { mtsUrl } from "../lib/mts-url";
import {
  RequestType,
  DesignProjectFields,
  WebUpdateFields,
  SocialMediaPostFields,
  NewRequestFormProps,
  EventFields,
  MarketingCampaignFields,
  BusinessCardFields
} from "../types/request.types";
import useTempId from "../hooks/use-temp-id";

type NewRequestFields =
  DesignProjectFields &
  WebUpdateFields &
  SocialMediaPostFields &
  EventFields &
  MarketingCampaignFields &
  BusinessCardFields;

interface NewRequestProps {
  formComponent: React.ComponentType<NewRequestFormProps>;
  requestType: RequestType;
}

export default function NewRequest(props: NewRequestProps) {
  const tempId = useTempId();
  const {
    formComponent: FormComponent,
    requestType
  } = props;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const reqTypeWords = requestType.split('_').slice(0, -1);
  const reqTitle = reqTypeWords.map(w => capitalize(w)).join(' ');

  const addRequest = useMutation({
    mutationFn:  (newRequest: NewRequestFields) => {
      const pathBase = pluralize(reqTypeWords.join('_'));
      
      return axios.post(
        mtsUrl(`${pathBase}/create`),
        {
          [requestType]: newRequest,
          temp_attachable_id: tempId
        },
        {
          withCredentials: true,
          headers: { 'X-CSRF-Token': csrfToken() }
        }
      )
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['requests', 'all'] });
      queryClient.invalidateQueries({
        queryKey: ['requests', `type:${reqTitle}`]
      });
      
      notifications.show({
        message: `The ${reqTitle} was successfully created.`,
        icon: <IconCheck />,
        color: 'teal',
        position: 'top-center'
      });

      navigate('/requests');
    },
    onError: (error, variables, context) => {
      notifications.show({
        message: `There was an error creating the ${reqTitle}.`,
        icon: <IconX />,
        color: 'red',
        position: 'top-center'
      });
    }
  });

  const onFormSubmit = useCallback((newRequest: NewRequestFields) => (
    addRequest.mutate(newRequest)
  ), []);
  
  return (
    <Paper p={18} radius={0} mih="100vh">
      <Title order={1}>New {reqTitle}</Title>
      <FormComponent onSubmit={onFormSubmit} tempId={tempId} />
    </Paper>
  );
}